import React from 'react';
import { Link } from 'react-router-dom';
import { X, Trash2, Plus, Minus, ShoppingBag, MessageCircle } from 'lucide-react';
import { useCart } from '../contexts/CartContext';
import { useSettings } from '../contexts/SettingsContext';
import { cloudinaryUrl } from '../lib/cloudinary';

const BASE = process.env.REACT_APP_BACKEND_URL;
function resolveUrl(u) { return (!u || u.startsWith('http')) ? u : `${BASE}${u}`; }
function imgUrl(u, w) { return cloudinaryUrl(resolveUrl(u), { width: w }); }

export default function CartDrawer() {
  const { items, remove, updateQty, clear, total, count, open, setOpen } = useCart();
  const { waLink } = useSettings();

  const buildWhatsAppMsg = () => {
    if (!items.length) return '';
    const lines = items.map(i =>
      `• *${i.product.name}* × ${i.qty} = ₹${(i.product.price * i.qty).toLocaleString('en-IN')}`
    );
    const msg = [
      'Hi KIRAQ! 👋 I want to order the following:',
      '',
      ...lines,
      '',
      `*Total: ₹${total.toLocaleString('en-IN')}*`,
      '',
      'Please confirm availability and share payment details. Thank you!',
    ].join('\n');
    return waLink(msg);
  };

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/40 z-40 backdrop-blur-[2px]"
        onClick={() => setOpen(false)}
      />

      {/* Drawer */}
      <div className="fixed top-0 right-0 h-full w-full max-w-sm bg-[#F6F3EE] z-50 flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#DDD7CE]">
          <div className="flex items-center gap-2">
            <ShoppingBag size={16} className="text-[#C6A978]" />
            <h2 className="font-['Cormorant_Garamond'] text-lg font-medium text-[#1E1C1A]">
              Your Bag
            </h2>
            {count > 0 && (
              <span className="bg-[#C6A978] text-white text-[10px] w-5 h-5 rounded-full flex items-center justify-center font-medium">
                {count}
              </span>
            )}
          </div>
          <button onClick={() => setOpen(false)} className="p-1 text-[#7A7369] hover:text-[#1E1C1A] transition-colors">
            <X size={20} />
          </button>
        </div>

        {/* Items */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center py-16">
              <ShoppingBag size={40} className="text-[#DDD7CE] mb-4" />
              <p className="font-['Cormorant_Garamond'] text-xl text-[#7A7369] mb-1">Your bag is empty</p>
              <p className="text-xs text-[#7A7369] mb-6">Add pieces you love</p>
              <button
                onClick={() => setOpen(false)}
                className="text-[10px] uppercase tracking-[0.2em] text-[#1E1C1A] border-b border-[#1E1C1A] pb-0.5"
              >
                Continue Shopping
              </button>
            </div>
          ) : (
            items.map(({ product, qty }) => (
              <div key={product.id} className="flex gap-3 pb-4 border-b border-[#DDD7CE]/60">
                <Link to={`/product/${product.id}`} onClick={() => setOpen(false)} className="flex-shrink-0">
                  <div className="w-20 h-24 overflow-hidden bg-[#E8E1D8]">
                    <img
                      src={imgUrl(product.image_url, 160)}
                      alt={product.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </Link>
                <div className="flex-1 min-w-0">
                  <Link to={`/product/${product.id}`} onClick={() => setOpen(false)}>
                    <p className="font-['Cormorant_Garamond'] text-sm font-medium text-[#1E1C1A] leading-tight mb-0.5 hover:text-[#C6A978] transition-colors">
                      {product.name}
                    </p>
                  </Link>
                  <p className="text-xs text-[#7A7369] mb-2">₹{product.price.toLocaleString('en-IN')} each</p>
                  <div className="flex items-center justify-between">
                    {/* Qty controls */}
                    <div className="flex items-center border border-[#DDD7CE]">
                      <button
                        onClick={() => updateQty(product.id, qty - 1)}
                        className="w-7 h-7 flex items-center justify-center text-[#7A7369] hover:text-[#1E1C1A] transition-colors"
                      >
                        <Minus size={11} />
                      </button>
                      <span className="w-8 text-center text-xs font-medium text-[#1E1C1A]">{qty}</span>
                      <button
                        onClick={() => updateQty(product.id, qty + 1)}
                        className="w-7 h-7 flex items-center justify-center text-[#7A7369] hover:text-[#1E1C1A] transition-colors"
                      >
                        <Plus size={11} />
                      </button>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-[#1E1C1A]">
                        ₹{(product.price * qty).toLocaleString('en-IN')}
                      </span>
                      <button
                        onClick={() => remove(product.id)}
                        className="p-1 text-[#7A7369] hover:text-red-500 transition-colors"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div className="border-t border-[#DDD7CE] px-5 py-4 space-y-3 bg-[#F6F3EE]">
            <div className="flex justify-between items-center">
              <span className="text-xs uppercase tracking-[0.15em] text-[#7A7369]">Subtotal</span>
              <span className="font-['Cormorant_Garamond'] text-lg font-medium text-[#1E1C1A]">
                ₹{total.toLocaleString('en-IN')}
              </span>
            </div>
            <p className="text-[10px] text-[#7A7369]">Free shipping above ₹499 · Ships in 2–3 days</p>
            <a
              href={buildWhatsAppMsg()}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpen(false)}
              className="flex items-center justify-center gap-2 w-full bg-[#25D366] text-white py-3.5 text-xs tracking-[0.2em] uppercase hover:bg-[#1ea855] transition-colors font-medium"
            >
              <MessageCircle size={15} />
              Order via WhatsApp
            </a>
            <button
              onClick={clear}
              className="w-full text-[10px] uppercase tracking-[0.15em] text-[#7A7369] hover:text-red-500 transition-colors py-1"
            >
              Clear Bag
            </button>
          </div>
        )}
      </div>
    </>
  );
}